import {
  isTraversalBuilderStateWithCurrentEditableRule,
  TraversalBuilderState,
  TraversalBuilderStateWithCurrentEditableRule,
} from '../types';
import {
  createEmptyPathCollapsingRule,
  defaultCollapsedPathReferenceStyle,
} from './createEmptyPathCollapsingRule';
import { groupBy, omit, pick } from 'lodash';
import { EditablePathCollapsingRule } from './types';
import {
  transformCurrentEditableRule,
  transformRules,
} from './collapsePathsViewStateTransformations';
import {
  DirectedTripleWithFiltersWithGraphIds,
  PathCollapsingRule,
  PathCollapsingRecursion,
  PathCollapsingRuleNonNullablePath,
  PathMatch,
} from '@ardoq/api-types';
import { getShortestPath, ShortestPath } from './getShortestPath';
import { MetamodelViewGraphEdge, MetaModelViewGraphNode } from '@ardoq/graph';
import { logError } from '@ardoq/logging';
import { viewpointBuilderGraphOperations } from '../viewpointBuilderGraphOperations';
import { namedDirectedTripleToBackendFormat } from '../traversals/namedDirectedTripleToBackendFormat';
import {
  CollapsingRuleIsActiveToggledPayload,
  CollapsingRuleToggledPayload,
} from './collapsePathsActions';
import { editTraversalOperations } from 'viewpointBuilder/traversals/editTraversalOperations';
import { ardoqId, partialPayload, pipeReducers } from '@ardoq/common-helpers';
import {
  assignRegionsToRules,
  getMatchingPaths,
  keepLongestMatchPerEndPosition,
  validateMatchesAgainstOverlaps,
} from 'traversals/pathAndGraphUtils';
import {
  CollapsingRuleErrorCode,
  CollapsingRuleErrorMessage,
} from './CollapsingRuleErrorMessage';

const setPathCollapsingRules = (
  state: TraversalBuilderState,
  rules: PathCollapsingRule[]
): TraversalBuilderState => {
  return pipeReducers(
    {
      ...state,
      initialPathCollapsingRules: rules,
    },
    partialPayload(processCollapsingRules, rules),
    viewpointBuilderGraphOperations.resetPathHighlightingForCollapsingRules,
    editTraversalOperations.updateEdgesToBeRemovedOnCollapsing,
    editTraversalOperations.updateStartNodeCountForCollapsedHierarchy,
    editTraversalOperations.updateTripleOptions
  );
};

const createPathCollapsingRule = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const newRule = editTraversalOperations.toInternalPathCollapsingRule(
    state,
    createEmptyPathCollapsingRule()
  );
  return {
    ...state,
    pathCollapsing: {
      ...state.pathCollapsing,
      currentEditableRule: {
        ...structuredClone(newRule),
        startNodeId: null,
        errorMessage: null,
      },
      rules: [...state.pathCollapsing.rules, newRule],
    },
    highlightedPaths: {
      ...state.highlightedPaths,
      [newRule.referenceTypeId]: [],
    },
  };
};

const savePathCollapsingRule = (
  state: TraversalBuilderState,
  editableRule: EditablePathCollapsingRule
): TraversalBuilderState => {
  const newBaseRule = getNewRule(editableRule);
  if (!newBaseRule) {
    logError(Error('Expected newBaseRule to exist'));
    return state;
  }
  const isExpanded = true;
  const includePathIndexInEqualityCheck = false;
  const newRule = editTraversalOperations.toInternalPathCollapsingRule(
    state,
    newBaseRule,
    isExpanded,
    includePathIndexInEqualityCheck
  );

  const newHighlightedPaths = {
    ...state.highlightedPaths,
    [newRule.referenceTypeId]:
      state.highlightedPaths[editableRule.referenceTypeId] ?? [],
  };
  return pipeReducers(
    {
      ...state,
      highlightedPaths: omit(newHighlightedPaths, editableRule.referenceTypeId),
      pathCollapsing: {
        ...state.pathCollapsing,
        rules: state.pathCollapsing.rules.map(rule =>
          rule.referenceTypeId === editableRule.referenceTypeId ? newRule : rule
        ),
        currentEditableRule: null,
      },
    },
    // Re-resolve every rule's collapsed edges against the full set, so existing
    // rules account for the newly saved one (and vice versa) when they overlap.
    resolveAllVirtualEdgeStates,
    viewpointBuilderGraphOperations.resetPathHighlightingForCollapsingRules,
    editTraversalOperations.updateEdgesToBeRemovedOnCollapsing,
    editTraversalOperations.updateStartNodeCountForCollapsedHierarchy
  );
};

const getNewRule = (
  editableRule: EditablePathCollapsingRule
): PathCollapsingRuleNonNullablePath | null => {
  const newRule = pick(editableRule, [
    'path',
    'displayText',
    'referenceStyle',
    'isActive',
    'referenceTypeId',
  ]);
  if (!newRule.path) {
    return null;
  }
  newRule.path = newRule.path.map(triple => ({
    ...triple,
    referenceType: namedDirectedTripleToBackendFormat(triple).referenceType,
  }));
  newRule.referenceTypeId = ardoqId();

  return {
    ...newRule,
    ...(editableRule.collapseRecursively
      ? { collapseRecursively: editableRule.collapseRecursively }
      : {}),
  } as PathCollapsingRuleNonNullablePath;
};

const deletePathCollapsingRule = (
  state: TraversalBuilderState,
  referenceTypeId: string
): TraversalBuilderState => {
  const newState = viewpointBuilderGraphOperations.updatePathHighlighting(
    state,
    {
      shortestPath: null,
      highlightedPathId: referenceTypeId,
      clearSelectedNodes: true,
    }
  );
  const { highlightedPaths, pathCollapsing } = newState;
  const newHighlightedPaths = omit(highlightedPaths, referenceTypeId);
  return {
    ...newState,
    highlightedPaths: newHighlightedPaths,
    pathCollapsing: {
      ...pathCollapsing,
      rules: pathCollapsing.rules.filter(
        rule => rule.referenceTypeId !== referenceTypeId
      ),
    },
  };
};

const editPathCollapsingRule = (
  state: TraversalBuilderState,
  referenceTypeId: string
): TraversalBuilderState => {
  const rule = getRuleByTypeId(state, referenceTypeId);
  if (!rule) {
    return state;
  }
  return {
    ...state,
    pathCollapsing: {
      ...state.pathCollapsing,
      currentEditableRule: {
        ...structuredClone(rule),
        startNodeId: null,
        errorMessage: null,
      },
    },
  };
};

const cancelEditPathCollapsingRule = (
  state: TraversalBuilderState,
  editableRule: EditablePathCollapsingRule
): TraversalBuilderState => {
  const currentRuleBeingEdited = getRuleByTypeId(
    state,
    editableRule.referenceTypeId
  );

  const newState = viewpointBuilderGraphOperations.updatePathHighlighting(
    state,
    {
      shortestPath: null,
      highlightedPathId: editableRule.referenceTypeId,
    }
  );
  clearAllSelectedNodes(newState.graphNodes);

  if (!currentRuleBeingEdited?.path.length) {
    return viewpointBuilderGraphOperations.resetPathHighlightingForCollapsingRules(
      {
        ...newState,
        pathCollapsing: {
          ...newState.pathCollapsing,
          rules: newState.pathCollapsing.rules.filter(
            rule => rule.referenceTypeId !== editableRule.referenceTypeId
          ),
          currentEditableRule: null,
        },
      }
    );
  }
  return viewpointBuilderGraphOperations.resetPathHighlightingForCollapsingRules(
    {
      ...newState,
      pathCollapsing: {
        ...newState.pathCollapsing,
        currentEditableRule: null,
      },
    }
  );
};

/**
 * How many hierarchy levels the rule leaves as they are. Only a recursive rule
 * absorbs the hierarchy, so only there is the number meaningful.
 */
const setKeepExpandedLevels = (
  state: TraversalBuilderState,
  keepExpandedLevels: number
): TraversalBuilderState => {
  const { currentEditableRule } = state.pathCollapsing;
  if (!currentEditableRule?.collapseRecursively) {
    return state;
  }
  return {
    ...state,
    pathCollapsing: {
      ...state.pathCollapsing,
      currentEditableRule: {
        ...currentEditableRule,
        collapseRecursively: {
          ...currentEditableRule.collapseRecursively,
          // A blank input parses to NaN, which would survive the clamp and end
          // up in the rule, where it stops the collapsing from applying at all.
          keepExpandedLevels: Number.isFinite(keepExpandedLevels)
            ? Math.max(0, Math.trunc(keepExpandedLevels))
            : 0,
        },
      },
    },
  };
};

const setDisplayName = (
  state: TraversalBuilderState,
  displayText: string
): TraversalBuilderState => {
  // currentEditableRule is never undefined here, but TS doesn't know that
  if (!state.pathCollapsing.currentEditableRule) {
    return state;
  }

  return {
    ...state,
    pathCollapsing: {
      ...state.pathCollapsing,
      currentEditableRule: {
        ...state.pathCollapsing.currentEditableRule,
        displayText,
      },
    },
  };
};

const isSelectingStartNode = (
  state: TraversalBuilderState
): state is TraversalBuilderStateWithCurrentEditableRule => {
  const isEditableRule = isTraversalBuilderStateWithCurrentEditableRule(state);
  if (!isEditableRule) {
    return false;
  }
  const {
    pathCollapsing: { currentEditableRule },
  } = state;
  return Boolean(
    !currentEditableRule.startNodeId && currentEditableRule.path.length === 0
  );
};

const shouldIgnoreNodeSelection = (
  { pathCollapsing }: TraversalBuilderState,
  nodeId: string | null
) =>
  !pathCollapsing.currentEditableRule ||
  pathCollapsing.currentEditableRule.startNodeId === nodeId ||
  pathCollapsing.currentEditableRule.path.length !== 0;

/**
 * A path anchored on the start node while the start set is expanded collapses
 * the hierarchy the expansion traverses, not only the hops the path spans: the
 * connections of every descendant end up on the root.
 */
const collapsesHierarchy = (
  { startSetExpansion, startNode }: TraversalBuilderState,
  { startNodeId, endNodeId }: ShortestPath
) =>
  startSetExpansion === 'descendants' &&
  startNode !== null &&
  (startNodeId === startNode.id || endNodeId === startNode.id);

const getPathCollapsingRecursion = (
  state: TraversalBuilderState,
  shortestPath: ShortestPath
): PathCollapsingRecursion | undefined => {
  if (!collapsesHierarchy(state, shortestPath)) {
    return undefined;
  }
  return {
    strategy: 'startSetExpansionDescendants',
    // A newly picked path starts with the whole hierarchy collapsed. Changing
    // the path means clearing the selection first, which drops the recursion and
    // the level count with it, so there is nothing to carry over here.
    keepExpandedLevels: 0,
  };
};

const getErrorMessage = (
  state: TraversalBuilderState,
  shortestPath: ShortestPath | null
) => {
  if (!shortestPath || !stateIsTraversalBuilderStateWithStartNode(state)) {
    return null;
  }

  // Such a path hides the hierarchy rather than components between its ends, so
  // a single hop is enough.
  if (
    shortestPath.path.length < 2 &&
    !collapsesHierarchy(state, shortestPath)
  ) {
    return CollapsingRuleErrorMessage.pathMustHideAtLeastOneComponent;
  }

  const collapsedComponentIds = getCollapsedNodeIds(
    state.graphEdges,
    shortestPath
  );

  if (collapsedComponentIds.has(state.startNode.id)) {
    return CollapsingRuleErrorMessage.collapsingPathHidesStartNode(
      state.startNode.getLabel()
    );
  }

  if (
    !(
      shortestPath.startNodeId === state.startNode.id ||
      shortestPath.endNodeId === state.startNode.id
    )
  ) {
    const errorMessage = getErrorMessageForDividingPath(state, shortestPath);
    if (errorMessage) {
      return errorMessage;
    }
  }

  const overlapError = getErrorMessageForUnresolvableOverlap(
    state,
    shortestPath
  );

  if (overlapError) {
    return overlapError;
  }

  return null;
};

// Build a temporary rule from the path the user is currently selecting, so it
// can be matched against the traversal paths alongside the existing rules.
const toCandidateRule = (
  shortestPath: ShortestPath
): PathCollapsingRuleNonNullablePath => ({
  referenceTypeId: '',
  displayText: '',
  referenceStyle: defaultCollapsedPathReferenceStyle,
  isActive: true,
  path: shortestPath.path.map(triple => ({
    ...triple,
    referenceType: namedDirectedTripleToBackendFormat(triple).referenceType,
  })),
});

const getResolvedMatchesGroupedByPath = (
  pathsWithGraphIds: DirectedTripleWithFiltersWithGraphIds[][],
  rules: PathCollapsingRuleNonNullablePath[]
) => {
  const includePathIndexInEqualityCheck = true;
  const withValidation = false;
  const matches = assignRegionsToRules(
    rules.flatMap(rule =>
      getMatchingPaths(
        pathsWithGraphIds,
        rule,
        includePathIndexInEqualityCheck,
        withValidation
      )
    )
  );
  // Resolve nested same-end overlaps the same way they are resolved when the
  // rules are applied, then keep only the matches that survive per path.
  return Object.values(groupBy(matches, 'pathIndex')).map(matchesForPath =>
    keepLongestMatchPerEndPosition(matchesForPath).map(({ match }) => match)
  );
};

const hasUnresolvableOverlap = (matchesGroupedByPath: PathMatch[][]) =>
  !matchesGroupedByPath.every(validateMatchesAgainstOverlaps);

// A collapsing path may overlap with itself or with another rule as long as the
// overlapping matches are nested with the same end position: in that case the
// longest match wins (see keepLongestMatchPerEndPosition) and the shorter,
// shadowed matches are dropped when the rules are applied. We only reject a rule
// when, after that resolution, a genuine "crossing" overlap remains — one where
// it is unclear which part of the path should be collapsed.
const getErrorMessageForUnresolvableOverlap = (
  state: TraversalBuilderState,
  shortestPath: ShortestPath
): CollapsingRuleErrorCode | null => {
  const candidateRule = toCandidateRule(shortestPath);
  const pathsWithGraphIds = editTraversalOperations.getTraversals(
    state,
    null,
    true
  ) as DirectedTripleWithFiltersWithGraphIds[][];
  const existingActiveRules = state.pathCollapsing.rules.filter(
    rule => rule.isActive && rule.path.length > 0
  );

  // The candidate rule on its own must not produce a crossing overlap on any
  // single path (e.g. A -> A -> A matching A -> A -> A -> A -> A in two
  // crossing positions).
  if (
    hasUnresolvableOverlap(
      getResolvedMatchesGroupedByPath(pathsWithGraphIds, [candidateRule])
    )
  ) {
    return CollapsingRuleErrorMessage.overlappingWithItself;
  }

  // Combined with the already-defined rules, the candidate must not introduce a
  // crossing overlap that cannot be resolved by keeping the longest match.
  if (
    hasUnresolvableOverlap(
      getResolvedMatchesGroupedByPath(pathsWithGraphIds, [
        candidateRule,
        ...existingActiveRules,
      ])
    )
  ) {
    return CollapsingRuleErrorMessage.partiallyOverlappingPath;
  }

  return null;
};

type TraversalBuilderStateWithStartNode = TraversalBuilderState & {
  startNode: MetaModelViewGraphNode;
};

const stateIsTraversalBuilderStateWithStartNode = (
  state: TraversalBuilderState
): state is TraversalBuilderStateWithStartNode => {
  return Boolean(state.startNode);
};

const getErrorMessageForDividingPath = (
  state: TraversalBuilderStateWithStartNode,
  shortestPath: ShortestPath
) => {
  const shortestPathStartNodeContext = getShortestPath(
    state,
    shortestPath.startNodeId,
    state.startNode.id
  );
  const shortestPathEndNodeContext = getShortestPath(
    state,
    shortestPath.endNodeId,
    state.startNode.id
  );
  if (!shortestPathStartNodeContext || !shortestPathEndNodeContext) {
    logError(
      Error(
        'Expected shortestPathStartNodeContext and shortestPathEndNodeContext to exist'
      )
    );
    return CollapsingRuleErrorMessage.unexpected;
  }

  // The shorter of both paths must be contained in the other path, otherwise
  // the selected path could divide the graph in two disconnected graphs.
  if (
    shortestPathStartNodeContext.path.length <
    shortestPathEndNodeContext.path.length
  ) {
    const collapsedComponentIds = getCollapsedNodeIds(
      state.graphEdges,
      shortestPathEndNodeContext
    );
    if (!collapsedComponentIds.has(shortestPath.startNodeId)) {
      return CollapsingRuleErrorMessage.dividingGraph;
    }
  } else {
    const collapsedComponentIds = getCollapsedNodeIds(
      state.graphEdges,
      shortestPathStartNodeContext
    );
    if (!collapsedComponentIds.has(shortestPath.endNodeId)) {
      return CollapsingRuleErrorMessage.dividingGraph;
    }
  }
  return null;
};

const getCollapsedNodeIds = (
  graphEdges: Map<string, MetamodelViewGraphEdge>,
  { startNodeId, endNodeId, pathByReferenceIds }: ShortestPath
) => {
  const collapsedComponentIds = new Set<string>(
    pathByReferenceIds.flatMap(id => {
      const edge = graphEdges.get(id);
      if (!edge) return [];
      const { source, target } = edge;
      return [source, target];
    })
  );
  collapsedComponentIds.delete(startNodeId);
  collapsedComponentIds.delete(endNodeId);

  return collapsedComponentIds;
};

const clearAllSelectedNodes = (
  graphNodes: Map<string, MetaModelViewGraphNode>
) =>
  Array.from(graphNodes.values()).forEach(graphNode => {
    graphNode.setIsSelected(false);
  });

const nodeSelected = (
  state: TraversalBuilderState,
  nodeId: string
): TraversalBuilderState => {
  if (shouldIgnoreNodeSelection(state, nodeId)) {
    return state;
  }
  if (isSelectingStartNode(state)) {
    const newGraphNodes = new Map(state.graphNodes);
    clearAllSelectedNodes(newGraphNodes);
    const node = newGraphNodes.get(nodeId);
    if (node) {
      node.setIsSelected(true);
    } else {
      logError(Error('Expected node to exist'));
      return state;
    }
    return {
      ...state,
      pathCollapsing: {
        ...state.pathCollapsing,
        currentEditableRule: {
          ...state.pathCollapsing.currentEditableRule,
          startNodeId: nodeId,
        },
      },
    };
  }

  const { currentEditableRule } = state.pathCollapsing;

  if (!currentEditableRule) {
    logError(Error('Expected currentEditableRule to exist'));
    return state;
  }

  const shortestPath = getShortestPath(
    state,
    currentEditableRule.startNodeId!,
    nodeId
  );

  if (!shortestPath) {
    return state;
  }

  const errorMessage = getErrorMessage(state, shortestPath);
  const { path } = shortestPath;

  const newCurrentEditableRule = {
    ...currentEditableRule,
    errorMessage,
    path,
    collapseRecursively: getPathCollapsingRecursion(state, shortestPath),
  };
  const newState = viewpointBuilderGraphOperations.updatePathHighlighting(
    state,
    {
      shortestPath,
      highlightedPathId: currentEditableRule.referenceTypeId,
      currentEditableRule: newCurrentEditableRule,
      hasError: Boolean(errorMessage),
    }
  );
  newState.graphNodes.get(nodeId)!.setIsSelected(true);
  return newState;
};

const getPathCollapsingViewState = (state: TraversalBuilderState) => {
  const { pathCollapsing, graphNodes } = state;
  const isHierarchyWildcardActive = Boolean(state.startSetExpansionState);

  return {
    currentEditableRule: pathCollapsing.currentEditableRule
      ? transformCurrentEditableRule(
          pathCollapsing.currentEditableRule,
          graphNodes,
          isHierarchyWildcardActive
        )
      : null,
    rules: transformRules(
      pathCollapsing.rules,
      graphNodes,
      isHierarchyWildcardActive
    ),
  };
};

const clearCollapsedPathNodes = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  if (!state.pathCollapsing.currentEditableRule) {
    return state;
  }
  const newState = viewpointBuilderGraphOperations.updatePathHighlighting(
    state,
    {
      shortestPath: null,
      highlightedPathId:
        state.pathCollapsing.currentEditableRule.referenceTypeId,
    }
  );
  clearAllSelectedNodes(newState.graphNodes);
  return {
    ...newState,
    pathCollapsing: {
      ...state.pathCollapsing,
      currentEditableRule: {
        ...state.pathCollapsing.currentEditableRule,
        path: [],
        startNodeId: null,
        // Nothing is selected any more, so nothing collapses the hierarchy.
        collapseRecursively: undefined,
      },
    },
  };
};

const getRuleByTypeId = (
  state: TraversalBuilderState,
  referenceTypeId: string
) =>
  state.pathCollapsing.rules.find(
    rule => rule.referenceTypeId === referenceTypeId
  );

const onGraphNodeHover = (
  state: TraversalBuilderState,
  nodeId: string | null
): TraversalBuilderState => {
  if (shouldIgnoreNodeSelection(state, nodeId) || isSelectingStartNode(state)) {
    return state;
  }

  const { currentEditableRule } = state.pathCollapsing;

  if (!currentEditableRule) {
    logError(Error('Expected currentEditableRule to exist'));
    return state;
  }

  const shortestPath = nodeId
    ? getShortestPath(state, currentEditableRule.startNodeId!, nodeId)
    : null;

  const errorMessage = getErrorMessage(state, shortestPath);

  const newCurrentEditableRule = {
    ...currentEditableRule,
    errorMessage,
    // The hovered path is previewed as it would be collapsed, hierarchy
    // included. Hovering away clears it again.
    collapseRecursively: shortestPath
      ? getPathCollapsingRecursion(state, shortestPath)
      : undefined,
  };

  return viewpointBuilderGraphOperations.updatePathHighlighting(state, {
    shortestPath,
    highlightedPathId: currentEditableRule.referenceTypeId,
    isFadedOut: Boolean(shortestPath),
    currentEditableRule: newCurrentEditableRule,
    hasError: Boolean(errorMessage),
  });
};

const collapsingRuleToggled = (
  state: TraversalBuilderState,
  { referenceTypeId, isExpanded }: CollapsingRuleToggledPayload
): TraversalBuilderState => {
  const rule = getRuleByTypeId(state, referenceTypeId);
  if (!rule) {
    return state;
  }
  return pipeReducers(
    {
      ...state,
      pathCollapsing: {
        ...state.pathCollapsing,
        rules: state.pathCollapsing.rules.map(r =>
          r.referenceTypeId === referenceTypeId ? { ...r, isExpanded } : r
        ),
      },
    },
    viewpointBuilderGraphOperations.resetPathHighlightingForCollapsingRules,
    editTraversalOperations.updateEdgesToBeRemovedOnCollapsing,
    editTraversalOperations.updateStartNodeCountForCollapsedHierarchy
  );
};

const onCollapsedPathTabUnselected = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  if (!state.startNode) {
    logError(Error('Impossible state, expected startNode to exist'));
    return state;
  }

  const collapsedState = pipeReducers(
    {
      ...state,
      pathCollapsing: {
        ...state.pathCollapsing,
        rules: state.pathCollapsing.rules.map(rule => ({
          ...rule,
          isExpanded: false,
        })),
      },
    },
    viewpointBuilderGraphOperations.resetPathHighlightingForCollapsingRules,
    editTraversalOperations.updateEdgesToBeRemovedOnCollapsing,
    editTraversalOperations.updateStartNodeCountForCollapsedHierarchy
  );

  const { collapsedGraphNodes } =
    editTraversalOperations.buildCollapsedGraph(collapsedState);
  const selectedNode =
    state.selectedGraphNodeId &&
    collapsedGraphNodes.has(state.selectedGraphNodeId)
      ? state.selectedGraphNodeId
      : state.startNode.id;

  return editTraversalOperations.toggleIsSelectedGraphNodeAndUpdateTripleOptions(
    collapsedState,
    {
      graphNodeId: selectedNode,
      isForceSelected: true,
    }
  );
};

const toggleCollapsingRuleIsActive = (
  state: TraversalBuilderState,
  { referenceTypeId, isActive }: CollapsingRuleIsActiveToggledPayload
) => {
  const rule = getRuleByTypeId(state, referenceTypeId);
  if (!rule) {
    return state;
  }
  return pipeReducers(
    {
      ...state,
      pathCollapsing: {
        ...state.pathCollapsing,
        rules: state.pathCollapsing.rules.map(r =>
          r.referenceTypeId === rule.referenceTypeId ? { ...r, isActive } : r
        ),
      },
    },
    processCollapsingRules,
    viewpointBuilderGraphOperations.resetPathHighlightingForCollapsingRules,
    editTraversalOperations.updateEdgesToBeRemovedOnCollapsing,
    editTraversalOperations.updateStartNodeCountForCollapsedHierarchy
  );
};

// Recompute every rule's collapsed edges with cross-rule overlap resolution,
// preserving each rule's isExpanded flag.
const resolveAllVirtualEdgeStates = (
  state: TraversalBuilderState
): TraversalBuilderState => ({
  ...state,
  pathCollapsing: {
    ...state.pathCollapsing,
    rules: editTraversalOperations.withResolvedVirtualEdgeStates(
      state,
      state.pathCollapsing.rules
    ),
  },
});

const processCollapsingRules = (
  state: TraversalBuilderState,
  rules: PathCollapsingRule[] = state.pathCollapsing.rules
): TraversalBuilderState => ({
  ...state,
  pathCollapsing: {
    ...state.pathCollapsing,
    // Resolve overlapping rules against each other so the builder graph only
    // shows the surviving (longest) collapsed paths.
    rules: editTraversalOperations.toInternalPathCollapsingRules(state, rules),
  },
});

export const collapsePathsOperations = {
  cancelEditPathCollapsingRule,
  clearCollapsedPathNodes,
  collapsingRuleToggled,
  createPathCollapsingRule,
  deletePathCollapsingRule,
  editPathCollapsingRule,
  getPathCollapsingViewState,
  nodeSelected,
  onCollapsedPathTabUnselected,
  onGraphNodeHover,
  savePathCollapsingRule,
  setDisplayName,
  setKeepExpandedLevels,
  setPathCollapsingRules,
  toggleCollapsingRuleIsActive,
};
